import { useState, useEffect } from 'react';

interface NumberInputProps {
  label: string;
  description?: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  prefix?: string;
  suffix?: string;
  disabled?: boolean;
}

export function NumberInput({
  label,
  description,
  value,
  onChange,
  min,
  max,
  step = 1,
  prefix,
  suffix,
  disabled = false,
}: NumberInputProps) {
  const [localValue, setLocalValue] = useState(value.toString());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLocalValue(value.toString());
  }, [value]);

  const validateAndUpdate = (inputValue: string) => {
    setLocalValue(inputValue);
    setError(null);

    if (inputValue === '' || inputValue === '-') {
      return;
    }

    const numValue = parseFloat(inputValue);

    if (isNaN(numValue)) {
      setError('Please enter a valid number');
      return;
    }

    if (min !== undefined && numValue < min) {
      setError(`Minimum value is ${min}`);
      return;
    }

    if (max !== undefined && numValue > max) {
      setError(`Maximum value is ${max}`);
      return;
    }

    onChange(numValue);
  };

  const handleBlur = () => {
    // Reset to last valid value on blur if current is invalid
    if (error || localValue === '' || localValue === '-') {
      setLocalValue(value.toString());
      setError(null);
    }
  };

  const increment = () => {
    if (disabled) return;
    const newValue = value + step;
    if (max === undefined || newValue <= max) {
      onChange(newValue);
    }
  };

  const decrement = () => {
    if (disabled) return;
    const newValue = value - step;
    if (min === undefined || newValue >= min) {
      onChange(newValue);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-200 mb-1">{label}</label>
      {description && (
        <p className="text-xs text-gray-400 mb-2">{description}</p>
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={decrement}
          disabled={disabled || (min !== undefined && value <= min)}
          className={`
            w-8 h-8 flex items-center justify-center rounded bg-gray-700 text-gray-300
            hover:bg-gray-600 transition-colors
            ${disabled || (min !== undefined && value <= min) ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          -
        </button>
        <div className="relative flex-1">
          {prefix && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              {prefix}
            </span>
          )}
          <input
            type="text"
            inputMode="decimal"
            value={localValue}
            onChange={(e) => validateAndUpdate(e.target.value)}
            onBlur={handleBlur}
            disabled={disabled}
            className={`
              w-full px-3 py-2 bg-gray-700 border rounded text-white text-center
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
              ${prefix ? 'pl-7' : ''}
              ${suffix ? 'pr-10' : ''}
              ${error ? 'border-red-500' : 'border-gray-600'}
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          />
          {suffix && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              {suffix}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={increment}
          disabled={disabled || (max !== undefined && value >= max)}
          className={`
            w-8 h-8 flex items-center justify-center rounded bg-gray-700 text-gray-300
            hover:bg-gray-600 transition-colors
            ${disabled || (max !== undefined && value >= max) ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          +
        </button>
      </div>
      {error && (
        <p className="text-xs text-red-400 mt-1">{error}</p>
      )}
      {min !== undefined && max !== undefined && !error && (
        <p className="text-xs text-gray-500 mt-1">
          Range: {prefix}{min}{suffix} - {prefix}{max}{suffix}
        </p>
      )}
    </div>
  );
}
